/**
 * Trend Color Utilities
 *
 * Provides brand-compliant color utilities for trend indicators across components.
 * These utilities ensure consistent use of chart colors for trend visualization.
 *
 * @example
 * ```typescript
 * import { getTrendColorClass, getTrendColorStyle } from '@/utils/trendColors';
 *
 * // Use CSS class approach
 * const cssClass = getTrendColorClass(5.2); // 'trend-positive'
 *
 * // Use inline style approach
 * const style = getTrendColorStyle(-2.8); // { color: 'var(--chart-negative)', backgroundColor: '...' }
 * ```
 */

/**
 * Trend type enumeration for better type safety
 */
export type TrendType = "positive" | "negative" | "neutral";

/**
 * Determines trend type based on numeric value
 * @param trendValue - The trend value (positive, negative, or zero)
 * @returns Trend type classification
 */
export function getTrendType(trendValue: number): TrendType {
  if (trendValue > 0) return "positive";
  if (trendValue < 0) return "negative";
  return "neutral";
}

/**
 * Gets the CSS class name for a trend value
 * @param trendValue - The trend value
 * @returns CSS class name following the pattern 'trend-{type}'
 */
export function getTrendColorClass(trendValue: number): string {
  const type = getTrendType(trendValue);
  return `trend-${type}`;
}

/**
 * Gets inline style object for trend colors
 * @param trendValue - The trend value
 * @param includeBackground - Whether to include background color (default: true)
 * @returns Style object with color and optional background-color
 */
export function getTrendColorStyle(
  trendValue: number,
  includeBackground: boolean = true
): Record<string, string> {
  const type = getTrendType(trendValue);
  const colorMap = {
    positive: {
      color: "var(--chart-positive, #39a275)",
      backgroundColor: "rgba(57, 162, 117, 0.1)",
    },
    negative: {
      color: "var(--chart-negative, #dc2626)",
      backgroundColor: "rgba(220, 38, 38, 0.1)",
    },
    neutral: {
      color: "var(--chart-neutral, #7F8C98)",
      backgroundColor: "rgba(127, 140, 152, 0.1)",
    },
  };

  const style: Record<string, string> = {
    color: colorMap[type].color,
  };

  if (includeBackground) {
    style.backgroundColor = colorMap[type].backgroundColor;
  }

  return style;
}

/**
 * Gets the appropriate Material Design icon for a trend
 * @param trendValue - The trend value
 * @returns Material Design icon name
 */
export function getTrendIcon(trendValue: number): string {
  if (trendValue > 0) return "mdi-arrow-up";
  if (trendValue < 0) return "mdi-arrow-down";
  return "mdi-minus";
}

/**
 * Formats trend value with appropriate prefix
 * @param trendValue - The trend value
 * @returns Formatted string with + for positive, - already included for negative
 */
export function formatTrendValue(trendValue: number): string {
  if (trendValue > 0) return `+${trendValue}`;
  return `${trendValue}`;
}

/**
 * Brand-compliant trend color constants for direct use
 * Updated to use new brand chart colors: Fire Engine Red, Vanilla, etc.
 */
export const TREND_COLORS = {
  positive: {
    color: "var(--chart-positive, #059669)",
    backgroundColor: "rgba(5, 150, 105, 0.1)",
  },
  negative: {
    color: "var(--chart-negative, #d62828)", // Fire Engine Red
    backgroundColor: "rgba(214, 40, 40, 0.1)", // Fire Engine Red with opacity
  },
  neutral: {
    color: "var(--chart-neutral, #eae2b7)", // Vanilla
    backgroundColor: "rgba(234, 226, 183, 0.1)", // Vanilla with opacity
  },
} as const;
